import { Controller, useForm, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type {
  MemberExternalAccountImportPreferencesSafe,
  MemberExternalAccountImportPreferencesUpdateInput,
  MemberExternalAccountSafe,
} from '@vivport/backend/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { useUpdateMemberExternalAccountImportPreferences } from '@/hooks';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';

const formSchema = z.object({
  messageLookbackDays: z
    .number()
    .int({ message: 'Enter a whole number' })
    .min(1, { message: 'Use 1 or more days, or leave blank for no limit' })
    .max(3650)
    .nullable(),
  messageMaxResults: z
    .number()
    .int({ message: 'Enter a whole number' })
    .min(1, { message: 'Must be at least 1' })
    .max(500, { message: 'Must be 500 or less' }),
  messageIncludeSpamTrash: z.boolean(),
  messageOnlyWithAttachments: z.boolean(),
  extractObligationsOnImport: z.boolean(),
  autoFileOnImport: z.boolean(),
  messageExcludeNoReply: z.boolean(),
  messageSyncCadence: z.enum(['MANUAL', 'HOURLY', 'EVERY_15_MINUTES']),
  messageExcludedSendersText: z.string(),
  messageExcludedDomainsText: z.string(),
  calendarLookbackDays: z.number().int({ message: 'Enter a whole number' }).min(0).max(3650),
  calendarLookaheadDays: z.number().int({ message: 'Enter a whole number' }).min(0).max(3650),
  calendarMaxResults: z
    .number()
    .int({ message: 'Enter a whole number' })
    .min(1, { message: 'Must be at least 1' })
    .max(500, { message: 'Must be 500 or less' }),
});

type ImportSettingsFormValues = z.infer<typeof formSchema>;

function toFormValues(p: MemberExternalAccountImportPreferencesSafe): ImportSettingsFormValues {
  return {
    messageLookbackDays: p.messageLookbackDays,
    messageMaxResults: p.messageMaxResults,
    messageIncludeSpamTrash: p.messageIncludeSpamTrash,
    messageOnlyWithAttachments: p.messageOnlyWithAttachments,
    extractObligationsOnImport: p.extractObligationsOnImport,
    autoFileOnImport: p.autoFileOnImport,
    messageExcludeNoReply: p.messageExcludeNoReply,
    messageSyncCadence: p.messageSyncCadence,
    messageExcludedSendersText: p.messageExcludedSenders.join(', '),
    messageExcludedDomainsText: p.messageExcludedDomains.join(', '),
    calendarLookbackDays: p.calendarLookbackDays,
    calendarLookaheadDays: p.calendarLookaheadDays,
    calendarMaxResults: p.calendarMaxResults,
  };
}

function parseExclusionList(text: string): string[] {
  return Array.from(
    new Set(
      text
        .split(/[,\n]/)
        .map((value) => value.trim().toLowerCase())
        .filter((value) => value.length > 0)
    )
  ).slice(0, 200);
}

type NumberFieldName =
  | 'messageLookbackDays'
  | 'messageMaxResults'
  | 'calendarLookbackDays'
  | 'calendarLookaheadDays'
  | 'calendarMaxResults';

function NumberField({
  control,
  name,
  label,
  id,
  allowNull,
  placeholder,
  hint,
}: {
  control: Control<ImportSettingsFormValues>;
  name: NumberFieldName;
  label: string;
  id: string;
  allowNull?: boolean;
  placeholder?: string;
  hint?: string;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <div className="space-y-1">
          <Label htmlFor={id} className="text-xs text-muted-foreground">
            {label}
          </Label>
          <Input
            id={id}
            type="number"
            inputMode="numeric"
            placeholder={placeholder}
            value={field.value === null || Number.isNaN(field.value) ? '' : String(field.value)}
            onChange={(event) =>
              field.onChange(
                event.target.value === '' ? (allowNull ? null : Number.NaN) : event.target.valueAsNumber
              )
            }
          />
          {fieldState.error ? (
            <p className="text-xs text-destructive">{fieldState.error.message}</p>
          ) : null}
          {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
        </div>
      )}
    />
  );
}

type SwitchFieldName =
  | 'messageIncludeSpamTrash'
  | 'messageOnlyWithAttachments'
  | 'extractObligationsOnImport'
  | 'autoFileOnImport'
  | 'messageExcludeNoReply';

function SwitchField({
  control,
  name,
  label,
  id,
  hint,
}: {
  control: Control<ImportSettingsFormValues>;
  name: SwitchFieldName;
  label: string;
  id: string;
  hint?: string;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col">
            <Label htmlFor={id} className="text-sm">
              {label}
            </Label>
            {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
          </div>
          <Switch id={id} checked={field.value} onCheckedChange={field.onChange} />
        </div>
      )}
    />
  );
}

function RadioField({
  control,
  name,
  label,
  id,
  options,
  hint,
}: {
  control: Control<ImportSettingsFormValues>;
  name: 'messageSyncCadence';
  label: string;
  id: string;
  options: Array<{ value: ImportSettingsFormValues['messageSyncCadence']; label: string }>;
  hint?: string;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">{label}</Label>
          <RadioGroup value={field.value} onValueChange={field.onChange}>
            {options.map((option) => (
              <div key={option.value} className="flex items-center gap-2">
                <RadioGroupItem id={`${id}-${option.value}`} value={option.value} />
                <Label htmlFor={`${id}-${option.value}`} className="text-sm font-normal">
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
          {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
        </div>
      )}
    />
  );
}

type MemberExternalAccountImportSettingsProps = {
  account: MemberExternalAccountSafe;
  memberId: number;
};

export function MemberExternalAccountImportSettings({
  account,
  memberId,
}: MemberExternalAccountImportSettingsProps) {
  const [open, setOpen] = useState(false);
  const update = useUpdateMemberExternalAccountImportPreferences(memberId);
  const { control, handleSubmit, reset } = useForm<ImportSettingsFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: toFormValues(account.importPreferences),
  });

  const onSubmit = handleSubmit((values) => {
    const input: MemberExternalAccountImportPreferencesUpdateInput = {
      messageLookbackDays: values.messageLookbackDays,
      messageMaxResults: values.messageMaxResults,
      messageIncludeSpamTrash: values.messageIncludeSpamTrash,
      messageOnlyWithAttachments: values.messageOnlyWithAttachments,
      extractObligationsOnImport: values.extractObligationsOnImport,
      autoFileOnImport: values.autoFileOnImport,
      messageExcludeNoReply: values.messageExcludeNoReply,
      messageSyncCadence: values.messageSyncCadence,
      messageExcludedSenders: parseExclusionList(values.messageExcludedSendersText),
      messageExcludedDomains: parseExclusionList(values.messageExcludedDomainsText),
      calendarLookbackDays: values.calendarLookbackDays,
      calendarLookaheadDays: values.calendarLookaheadDays,
      calendarMaxResults: values.calendarMaxResults,
    };
    update.mutate(
      { id: account.id, input },
      {
        onSuccess: (prefs) => {
          reset(toFormValues(prefs));
          toast.success('Import settings saved.');
        },
        onError: (error: unknown) => {
          toast.error(getAxiosErrorDisplayMessage(error, 'Failed to save import settings.'));
        },
      }
    );
  });

  const fieldPrefix = `import-settings-${account.id}`;

  return (
    <div className="mt-3 border-t pt-3">
      <Button type="button" variant="ghost" size="sm" onClick={() => setOpen((value) => !value)}>
        {open ? 'Hide import settings' : 'Import settings'}
      </Button>

      {open ? (
        <form onSubmit={onSubmit} className="mt-3 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <NumberField
              control={control}
              name="messageLookbackDays"
              id={`${fieldPrefix}-lookback`}
              label="Import mail from the last (days)"
              allowNull
              placeholder="No limit"
              hint="Leave blank for no limit; minimum 1 day."
            />
            <NumberField
              control={control}
              name="messageMaxResults"
              id={`${fieldPrefix}-max`}
              label="Max messages per import"
            />
          </div>

          <div className="space-y-2">
            <SwitchField
              control={control}
              name="messageIncludeSpamTrash"
              id={`${fieldPrefix}-spam`}
              label="Include spam & trash"
            />
            <SwitchField
              control={control}
              name="messageOnlyWithAttachments"
              id={`${fieldPrefix}-attachments`}
              label="Only messages with attachments"
            />
            <SwitchField
              control={control}
              name="messageExcludeNoReply"
              id={`${fieldPrefix}-noreply`}
              label="Skip no-reply senders"
            />
            <SwitchField
              control={control}
              name="extractObligationsOnImport"
              id={`${fieldPrefix}-extract`}
              label="Extract obligations on import"
              hint="Runs AI obligation extraction on each imported message. Off by default; uses AI and may add cost on large imports."
            />
            <SwitchField
              control={control}
              name="autoFileOnImport"
              id={`${fieldPrefix}-autofile`}
              label="Auto-file imported mail"
              hint="Move each imported conversation from Unknown into its best-matching Anchor, and into that Anchor's document when there is exactly one."
            />
            <RadioField
              control={control}
              name="messageSyncCadence"
              id={`${fieldPrefix}-sync-cadence`}
              label="Background mail sync"
              options={[
                { value: 'MANUAL', label: 'Manual only' },
                { value: 'HOURLY', label: 'Sync hourly (recommended)' },
                { value: 'EVERY_15_MINUTES', label: 'Every 15 minutes' },
              ]}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor={`${fieldPrefix}-excluded`} className="text-xs text-muted-foreground">
              Excluded senders
            </Label>
            <Controller
              control={control}
              name="messageExcludedSendersText"
              render={({ field }) => (
                <Input
                  id={`${fieldPrefix}-excluded`}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="news@example.com, no-reply@…"
                />
              )}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor={`${fieldPrefix}-excluded-domains`} className="text-xs text-muted-foreground">
              Excluded domains
            </Label>
            <Controller
              control={control}
              name="messageExcludedDomainsText"
              render={({ field }) => (
                <Input
                  id={`${fieldPrefix}-excluded-domains`}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="example.com, news.example.com"
                />
              )}
            />
            {account.provider === 'MICROSOFT' ? (
              <p className="text-xs text-muted-foreground">
                On Microsoft, sender and domain exclusions are applied by Vivport after fetching —
                Outlook can&apos;t filter them server-side, so they&apos;re enforced on our side.
              </p>
            ) : null}
          </div>

          {account.calendarImportCapable ? (
            <div className="grid gap-3 sm:grid-cols-3">
              <NumberField
                control={control}
                name="calendarLookbackDays"
                id={`${fieldPrefix}-cal-back`}
                label="Calendar — days back"
              />
              <NumberField
                control={control}
                name="calendarLookaheadDays"
                id={`${fieldPrefix}-cal-ahead`}
                label="Calendar — days ahead"
              />
              <NumberField
                control={control}
                name="calendarMaxResults"
                id={`${fieldPrefix}-cal-max`}
                label="Calendar — max events"
              />
            </div>
          ) : null}

          <Button type="submit" size="sm" disabled={update.isPending}>
            {update.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save import settings
          </Button>
        </form>
      ) : null}
    </div>
  );
}
